'use client'
import CompanyBlocker from '@/app/[lng]/components/BlockerModal/CompanyBlocker'
import CustomErrorBoundary from '@/app/[lng]/ErrorBoundary'
import { useGetCompanyMemberQuery } from '@/app/globalRedux/api/memberSlice'
import { useTranslation } from '@/app/i18n/client'
import { ContactInfoResponseType } from '@/app/types/companyContactUsType'
import { ContactType } from '@/app/types/ContactType'
import { Skeleton } from '@/components/ui/skeleton'
import { getContactUs } from '@/services/contactUsService'
import React, { useEffect, useState } from 'react'
import ContactUsForm from './ContactUsForm'
import MemberContactCard from './MemberContactCard'
import { RootState } from '@/app/globalRedux/store'
import { useSelector } from 'react-redux'
const ContactUs: React.FC<{ id: string }> = ({ id }) => {
  const [contactUs, setContactUs] = useState<ContactInfoResponseType>()
  const [showContent, setShowContent] = useState<boolean | null>(null)
  const { access_count, isBeneficiary, accessPer } = useSelector(
    (state: RootState) => state.user
  )
  const {
    data: contactMembers,
    isLoading,
    isError,
  } = useGetCompanyMemberQuery<{
    data: ContactType
    isLoading: boolean
    isError: boolean
  }>({
    companyFriendlyUrl: id,
  })
  const getCompanyContact = async () => {
    const contact: ContactInfoResponseType = await getContactUs(id)

    if (contact?.message === 'Access Exhausted') {
      setShowContent(false)
    } else {
      setContactUs(contact)
      setShowContent(true)
    }
  }
  useEffect(() => {
    getCompanyContact()
  }, [])
  const { t } = useTranslation()
  const loopArray = Array.from({ length: 3 })

  if (showContent === null) {
    return (
      <>
        <div className="border mb-4 w-[952px] mobile-width">
          <div className="p-4 flex flex-col gap-3 justify-center items-center">
            <Skeleton className="w-20 h-5" />
            <Skeleton className="w-[30%] h-5" />
            <Skeleton className="w-[40%] h-5 " />
          </div>
          <div className="grid grid-cols-7 h-[518px] bg-[#fafafa] shadow-[0_0_9px_0_rgba(0,0,0,0.14)] p-[1px] rounded-[5px]">
            <div className="col-span-4 px-10 py-11 gap-6 flex flex-col mobile:p-[15px]">
              <div className="flex flex-col gap-3">
                <Skeleton className="h-10 w-[60%]" />
                <Skeleton className="h-10 w-[20%]" />
              </div>
              <div className="flex flex-col gap-[18px]">
                <Skeleton className="h-10 w-[50%]" />
                <Skeleton className="h-10 w-[50%]" />
                <Skeleton className="h-10 w-[50%]" />
                <div className="">
                  <Skeleton className="h-10 w-20" />
                </div>
              </div>
            </div>
            <div className="col-span-3 h-full shadow-[-2px_0_7px_-2px_rgba(0,0,0,0.14)]">
              <Skeleton className="h-full w-full" />
            </div>
          </div>
        </div>
        <div className="border shadow-[0_0_6px_0_rgba(0,0,0,0.1)] mb-4 grid grid-cols-[auto_auto_auto] px-[25px] py-[31px] border-t border-solid border-[#eaeaea] mobile:p-[15px]">
          <Skeleton className="h-20 w-[60%]" />
          <Skeleton className="h-20 w-[60%]" />
        </div>
      </>
    )
  }
  return (
    <div className=" flex flex-col gap-6 w-[952px] mobile-width">
      {!showContent && (
        <CompanyBlocker
          access_count={access_count}
          isBeneficiary={isBeneficiary}
          accessPer={accessPer}
        />
      )}
      {/* Heading and Form */}
      <div className="flex flex-col w-full gap-12 items-center justify-center">
        {/* Heading */}
        <div className="flex flex-col items-center gap-4">
          <h1 className=" flex text-[28px] font-ns_bold text-primary_text">
            {t('Get in touch')}
          </h1>
          <div className="flex flex-col leading-[21px] text-[14px] text-center text-[#747474]">
            {t('We are very approachable and would love to speak to you.')}
            <br />{' '}
            {t(
              'Feel free to call, send us an email, or simply complete the enquiry form.'
            )}
          </div>
        </div>
        {/* Form */}
        <div className="w-full">
          <ContactUsForm
            id={id}
            company_id={contactUs?.contact_info?.id ?? ''}
            contactMembers={contactMembers?.contacts}
            latitude={contactUs?.contact_info?.address?.latitude ?? 1}
            longitude={contactUs?.contact_info?.address?.longitude ?? 1}
          />
        </div>
      </div>
      <div className="shadow-[0_0_6px_0_rgba(0,0,0,0.1)]">
        <div className="text-[16px] font-ns_bold py-[20px] px-[25px]">
          {t('Contact Us')}
        </div>
        <div className="grid grid-cols-[auto_auto_auto] px-[25px] py-[31px] border-t border-solid border-[#eaeaea] mobile:p-[15px]">
          <div>
            <div className="text-[14px] text-[#747474]">
              {t('Customer service')}
            </div>
            <div className="text-[16px] font-ns_bold text-grey_forty">
              {contactUs?.contact_info?.address?.country?.dial_code &&
                `${contactUs?.contact_info?.address?.country?.dial_code} - `}
              {contactUs?.contact_info?.phone}
            </div>
          </div>

          <div>
            <div className="text-[14px] text-[#747474]">{t('Email')}</div>
            <div className="text-[16px] font-ns_bold text-grey_forty">
              {contactUs?.contact_info?.email}
            </div>
          </div>
        </div>
      </div>
      <div className="text-[16px] font-ns_bold">{t('Our Members')}</div>
      <div className="flex flex-col justify-center gap-[30px]">
        {/* Company Member's Contact Card */}
        {contactMembers?.contacts?.length > 0 ? (
          <div className="grid grid-cols-2 mobile:grid-cols-1 gap-6 mb-12 mobile:mb-2">
            {contactMembers?.contacts?.map((member: any, index) => (
              <CustomErrorBoundary key={`member-${index}`}>
                <MemberContactCard member={member} />
              </CustomErrorBoundary>
            ))}
          </div>
        ) : !isLoading && !isError && contactMembers?.contacts?.length <= 0 ? (
          <div className="shadow-[0_0_6px_0_rgba(0,0,0,0.1)] p-16 mb-12 flex flex-col gap-2 items-center justify-center text-primary-foreground">
            {t('Members not available at this moment')}
          </div>
        ) : (
          <div className="grid grid-cols-2 mobile:grid-cols-1 gap-6 mb-12 mobile:mb-2 p-4">
            {loopArray.map((_, index) => (
              <div
                key={`skeleton_${index}`}
                className="flex gap-3 shadow-[0_0_6px_0_rgba(0,0,0,0.1)]"
              >
                <div className="p-4 w-full h-full">
                  <Skeleton className="w-full h-full" />
                </div>
                <div className="flex flex-col gap-1 p-4">
                  <Skeleton className="w-60 h-4" />
                  <Skeleton className="w-60 h-4" />
                  <Skeleton className="w-60 h-4" />
                  <Skeleton className="w-60 h-4" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ContactUs
